import * as React from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  useMediaQuery,
  Button,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LinkButton from "./LinkButton";
import { useRailsContext } from "../single-page/contexts/RailsContext";
import { Link, useLocation } from "react-router-dom";
import PrefeituraLogo from "~/images/illustrations/logo-prefeitura-do-recife.svg";
import { APP_ROUTES } from "~/single-page/constants/routes";

const navBarHeight = "88px";

const NavBar = ({ toggleDrawer }) => {
  const { isAuthenticated } = useRailsContext();
  const isMobile = useMediaQuery('(max-width:700px)');
  const isSmallMobile = useMediaQuery('(max-width:480px)');
  
  // Breakpoints para aparição gradual dos menus
  const showFirstMenu = useMediaQuery('(min-width:900px)'); // Voluntariado e Doação
  const showSecondMenu = useMediaQuery('(min-width:970px)'); // + Conheça ONGs e projetos
  const showThirdMenu = useMediaQuery('(min-width:1150px)'); // + Empresas Parceiras
  const showAllMenus = useMediaQuery('(min-width:1250px)'); // + Eventos
  
  const location = useLocation();

  // Função para verificar se o link está ativo
  const isActiveLink = (path) => {
    return location.pathname === path;
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        height: navBarHeight,
        display: "flex",
        alignItems: "center",
        flexDirection: "row",
        width: "100vw",
        zIndex: 1200, // Garantir que está acima de outros elementos
        backgroundColor: '#009FE3',
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "row",
          width: "100vw",
          justifyContent: "space-between",
          minHeight: navBarHeight,
          py: 1, // Adicionar padding vertical para centralizar melhor
        }}
      >
        {/* Container esquerdo - Menu + Logo alinhados */}
        <Box 
          sx={{ 
            display: "flex", 
            alignItems: isMobile ? "flex-end" : "center",
            gap: isMobile ? 0.1 : 1, // Espaçamento entre menu e logo
            mt: isMobile ? 2 : 0
          }}
        >
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="menu"
            sx={{ 
              display: isMobile ? 'flex' : 'none',
              p: 0.6, // Reduzir padding para melhor alinhamento
              ml: isMobile ? 0 : 3, // Margem apenas para desktop
            }}
            onClick={toggleDrawer}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo da Prefeitura */}
          <Typography
            variant="h6"
            component={Link}
            to={APP_ROUTES.ROOT}
            sx={{
              display: "flex",
              alignItems: "center", // Centralizar verticalmente
              textDecoration: "none",
              color: "inherit",
              ml: isMobile ? 0 : 3, // Margem apenas para desktop
            }}
          >
            <Box
              component="img"
              src={PrefeituraLogo} 
              alt="Logo da Prefeitura do Recife" 
              sx={{
                height: 'auto', 
                maxHeight: isSmallMobile ? '26px' : '42px',
                width: 'auto',
                mt: isSmallMobile ? -4 : 0, // ✅ SUBIR apenas a logo
              }}
            />
          </Typography>
        </Box>

        {/* Menu de navegação - aparição gradual */}
        <Box 
          sx={{ 
            display: { xs: 'none', md: 'flex' },
            gap: 4, 
            alignItems: 'center',
            flex: 1, // Ocupar espaço disponível
            justifyContent: 'center', // Centralizar o menu
          }}
        >
          {/* Primeiro menu - aparece a partir de 900px */}
          {showFirstMenu && (
            <Typography 
              component={Link}
              to={APP_ROUTES.MOVING_ACTIONS}
              variant="body1" 
              sx={{ 
                color: 'white', 
                textDecoration: isActiveLink(APP_ROUTES.MOVING_ACTIONS) ? 'underline' : 'none',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                fontWeight: isActiveLink(APP_ROUTES.MOVING_ACTIONS) ? 'bold' : 'normal',
                '&:hover': { opacity: 0.8 }
              }}
            >
              Voluntariado e Doação
            </Typography>
          )}

          {/* Segundo menu - aparece a partir de 1100px */}
          {showSecondMenu && (
            <Typography 
              component={Link}
              to={APP_ROUTES.FIND_YOUR_CAUSE}
              variant="body1" 
              sx={{ 
                color: 'white', 
                textDecoration: isActiveLink(APP_ROUTES.FIND_YOUR_CAUSE) ? 'underline' : 'none',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                fontWeight: isActiveLink(APP_ROUTES.FIND_YOUR_CAUSE) ? 'bold' : 'normal',
                '&:hover': { opacity: 0.8 }
              }}
            >
              Conheça ONGs e Projetos
            </Typography>
          )}

          {/* Terceiro menu - aparece a partir de 1300px */}
          {showThirdMenu && (
            <Typography 
              component={Link}
              to={APP_ROUTES.SPONSORS_COMPANIES}
              variant="body1" 
              sx={{ 
                color: 'white', 
                textDecoration: isActiveLink(APP_ROUTES.SPONSORS_COMPANIES) ? 'underline' : 'none',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                fontWeight: isActiveLink(APP_ROUTES.SPONSORS_COMPANIES) ? 'bold' : 'normal',
                '&:hover': { opacity: 0.8 }
              }}
            >
              Empresas Parceiras
            </Typography>
          )}

          {/* Quarto menu - aparece a partir de 1500px */}
          {showAllMenus && (
            <Typography 
              component={Link}
              to={APP_ROUTES.EVENTS}
              variant="body1" 
              sx={{ 
                color: 'white', 
                textDecoration: isActiveLink(APP_ROUTES.EVENTS) ? 'underline' : 'none',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                fontWeight: isActiveLink(APP_ROUTES.EVENTS) ? 'bold' : 'normal',
                '&:hover': { opacity: 0.8 }
              }}
            >
              Eventos
            </Typography>
          )}
        </Box>

        {/* Container direito - Botões de ação */}
        <Box 
          sx={{ 
            display: "flex", 
            alignItems: "center", 
            gap: 2,
            mt: isMobile ? 2 : 0
          }}
        >
          {/* Botão "Inscreva sua ONG" */}
          {!isAuthenticated && (
            <Button
              component="a"
              href={APP_ROUTES.REGISTER}
              variant="contained"
              sx={{ 
                display: { xs: 'none', sm: 'inline-flex' },
                backgroundColor: 'white',
                color: '#009FE3',
                fontWeight: 'bold',
                px: 3,
                textDecoration: 'none',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)'
                }
              }}
            >
              Inscreva sua ONG
            </Button>
          )}

          {/* Botão "Área da ONG" - Estilo específico para mobile */}
          {isSmallMobile ? (
            <Box
              component="a"
              href={APP_ROUTES.SIGN_IN}
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'underline',
                color: 'white',
                fontSize: '0.8rem',
                fontWeight: 'normal',
                gap: 0.5,
                '&:hover': {
                  opacity: 0.8
                }
              }}
            >
              Área da ONG
              <ArrowForwardIcon sx={{ fontSize: '1rem' }} />
            </Box>
          ) : (
            <Button
              component="a"
              href={APP_ROUTES.SIGN_IN}
              variant="contained"
              sx={{ 
                display: "inline-flex",
                backgroundColor: '#294BB6',
                color: 'white',
                fontWeight: 'bold',
                px: 3,
                textDecoration: 'none',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#1e3a8a'
                }
              }}
            >
              Área da ONG
            </Button>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default NavBar;